import React, { useEffect, useState } from "react";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import {
	Backdrop,
	Button,
	CircularProgress,
	Fade,
	FormControl,
	FormControlLabel,
	Grid,
	Modal,
	Radio,
	RadioGroup,
	Typography
} from "@material-ui/core";
import { legacyApiResourceUrl } from "@ploy-lib/core";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modal: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center"
		},
		paper: {
			backgroundColor: theme.palette.background.paper,
			boxShadow: theme.shadows[5],
			padding: theme.spacing(2, 4, 3),
			width: 400
		},
		headerText: {
			marginTop: 12,
			textAlign: "center",
			marginBottom: 12
		},
		formControl: {
			marginTop: 12,
			marginBottom: 12
		},
		submitButtonContainer: {
			justifyContent: "center",
			display: "flex"
		},
		submitButton: {
			marginTop: 12,
			backgroundColor: theme.palette.secondary.main,
			marginBottom: 12
		},
		submitButtonLabel: {
			color: theme.palette.secondary.contrastText
		}
	})
);

interface ReasonForDecisionModalProps {
	open: boolean;
	setOpen: (e: boolean) => void;
	confirmDecisionWithReason?: (reason: string) => Promise<void>;
	confirmDecisionWithAppActionEvent?: (event: any) => Promise<void>;
	decisionMode: "cancel" | "reject";
	appId: number;
}

interface ReasonForDecisionModalInternalWebWrapperProps {
	confirmDecisionWithAppActionEvent?: (event: any) => Promise<void>;
	confirmDecisionWithReason?: (reason: string) => Promise<void>;
	decisionMode: "cancel" | "reject";
	appId: number;
}

/* Use this component when using
	`DployMicroWeb.addPartial({
		...
	})`
 * in order to provide a wrapper that can pass props for handling open/closed-state
 */
export const ReasonForDecisionModalInternalWebWrapper = (
	props: ReasonForDecisionModalInternalWebWrapperProps
) => {
	const [open, setOpen] = useState(true);

	useEffect(() => setOpen(true), [props]);

	return <ReasonForDecisionModal setOpen={setOpen} open={open} {...props} />;
};

export const ReasonForDecisionModal = (props: ReasonForDecisionModalProps) => {
	const {
		open,
		setOpen,
		confirmDecisionWithAppActionEvent,
		confirmDecisionWithReason,
		appId,
		decisionMode
	} = props;

	const onCloseHandler = () => setOpen(false);

	const classes = useStyles();

	const [allReasons, setAllReasons] = useState<string[]>([]);
	const [reasonForDecision, setReasonForDecision] = useState("");
	const handleRadioBtnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setReasonForDecision((event.target as HTMLInputElement).value);
	};
	const [loading, setLoading] = useState(true);

	const doConfirm = async (event, reason): Promise<void> => {
		if (confirmDecisionWithReason) {
			confirmDecisionWithReason(reason);
		}
		if (confirmDecisionWithAppActionEvent) {
			confirmDecisionWithAppActionEvent(event);
		}
	};

	useEffect(() => {
		const groupCoode = decisionMode === "cancel" ? "Cancel" : "Reject";
		fetch(
			legacyApiResourceUrl(
				`StandardTextList/GetStandardTextsFromDt?code=ReasonFor${groupCoode}Opt`
			)
		).then(async res => {
			setAllReasons(await res.json());
			setLoading(false);
		});
	}, [decisionMode]);

	return (
		<Modal
			className={classes.modal}
			open={open}
			onClose={onCloseHandler}
			closeAfterTransition
			BackdropComponent={Backdrop}
			BackdropProps={{
				timeout: 500
			}}
		>
			<Fade in={open}>
				<div className={classes.paper}>
					<Grid
						container
						justifyContent="space-around"
						direction="column"
						alignContent="center"
					>
						<Grid item>
							<Typography variant="h5" className={classes.headerText}>
								<FormattedMessage
									id="reason-for-decision-modal.header-text"
									defaultMessage="Provide a reason"
									description="Header text of reason for decision modal"
								/>
							</Typography>
						</Grid>
						<Grid item xs={10}>
							{loading ? (
								<CircularProgress size="large" />
							) : allReasons.length > 0 ? (
								<FormControl variant="standard" className={classes.formControl}>
									<RadioGroup
										value={reasonForDecision}
										onChange={handleRadioBtnChange}
									>
										{allReasons.map((reason: string) => (
											<FormControlLabel
												value={reason}
												control={<Radio />}
												label={reason}
											/>
										))}
									</RadioGroup>
								</FormControl>
							) : (
								<FormattedMessage
									id="reason-for-decision-modal.error-text"
									defaultMessage="Failed to fetch reasons"
								/>
							)}
						</Grid>
						<Grid item className={classes.submitButtonContainer}>
							<Button
								className={classes.submitButton}
								onClick={event => {
									setOpen(false);
									doConfirm(event, reasonForDecision).then(async () => {
										//	Update UnderWritingDTO with 'ReasonForUpdate':
										await fetch(
											legacyApiResourceUrl(
												"UnderwritingData/UpdateReasonForDecision"
											),
											{
												method: "POST",
												headers: {
													Accept: "application/json",
													"Content-Type": "application/json"
												},
												body: JSON.stringify({
													reason: reasonForDecision,
													applicationId: appId
												})
											}
										);
									});
								}}
								disabled={
									loading || allReasons.length === 0 || reasonForDecision === ""
								}
							>
								<Typography className={classes.submitButtonLabel}>
									{decisionMode === "cancel" ? (
										<FormattedMessage
											id="reason-for-decision-modal.confirm-cancel-button"
											defaultMessage="Cancel application"
											description="Confirm cancel application text"
										/>
									) : (
										<FormattedMessage
											id="reason-for-decision-modal.confirm-reject-button"
											defaultMessage="Reject application"
											description="Confirm reject application text"
										/>
									)}
								</Typography>
							</Button>
						</Grid>
					</Grid>
				</div>
			</Fade>
		</Modal>
	);
};
